/**
 * `mockups/client/files.html` §2 — a project's own files, and the one gated thing
 * in the portal.
 *
 * `.docs/reports/16-report.md` §Files: "handover files on a work order are
 * released only when its final milestone invoice is settled, everything else is
 * released when delivered." So the `Locked` block is the exception on this page,
 * and it is the block that carries it — not the card. The block **names the
 * milestone that unlocks it**, because "held" with nothing to do about it is not
 * a state, it is a shrug.
 *
 * Held is not gated. A brief the client uploaded on a request is held for ever
 * (`kind: 'upload'`, `released_at` null) and is behind no payment at all; only a
 * `handover` on a work order reaches `locked`, and the read model has already
 * done that separation. A held row on this card says which of the two it is
 * (`fileType.heldLabel`) rather than the bare word both of them used to share.
 */

import type { FileEntryView, ProjectFilesView } from '@fareast/shared'
import { Link } from '@tanstack/react-router'

import { DownloadLink } from './DownloadLink.js'
import { Card, CardBody, CardHead, CardTitle } from '../../../components/portal/Card.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { FileRow, Locked } from '../../../components/portal/Files.js'
import { Push, Stack } from '../../../components/portal/Layout.js'
import { B, Linkish, More, Muted, Num, Ref, Small } from '../../../components/portal/Text.js'
import { useFormat, type Formatter } from '../../../format/useFormat.js'
import { extensionOf, heldLabel } from './fileType.js'

/** "4 handover items are held" — one string, so the verb agrees with the count. */
const HELD_ITEMS = { one: 'handover item is', other: 'handover items are' } as const

export type ProjectFilesProps = { readonly projects: ProjectFilesView[] }

export function ProjectFiles({ projects }: ProjectFilesProps) {
  if (projects.length === 0) {
    return (
      <Card>
        <CardBody>
          <Empty>No project files yet — a project's files appear here as it is delivered.</Empty>
        </CardBody>
      </Card>
    )
  }

  return (
    <Stack gap="loose">
      {projects.map((project) => (
        <ProjectCard key={project.work_order_ref} project={project} />
      ))}
    </Stack>
  )
}

function ProjectCard({ project }: { readonly project: ProjectFilesView }) {
  const format = useFormat()
  const locked = project.locked

  /*
   * No `need` on the card, and that is the mockup's own reading of P4.
   *
   * `Card`'s prop says "this whole panel is waiting on you", and nothing on this
   * one is: the released files are downloadable, milestone 3 is not delivered and
   * milestone 4's invoice does not exist yet, so there is no decision here and no
   * cost that has fallen due. `files.html`'s project card is a plain `.card` and
   * `.locked` carries its own neutral dashed border — the gate is named, in the
   * one block it applies to, rather than painted around three rows it does not.
   */
  return (
    <Card>
      <CardHead>
        <CardTitle>{project.title}</CardTitle>
        <Ref value={project.work_order_ref} />
        <Push />
        <More asChild>
          <Link to="/app/projects">Project and milestones →</Link>
        </More>
      </CardHead>

      {project.entries.length === 0 ? (
        <CardBody>
          <Empty>Nothing released on this project yet.</Empty>
        </CardBody>
      ) : (
        <CardBody tight>
          {project.entries.map((entry) => (
            <FileRow
              key={entry.id}
              icon={extensionOf(entry.name)}
              title={entry.title}
              sub={milestoneSubOf(entry, format)}
              meta={
                <>
                  <Num>{format.bytes(entry.size)}</Num>
                  {entry.released ? (
                    <DownloadLink id={entry.id} label={`Download ${entry.title}`} />
                  ) : (
                    <Muted>{heldLabel(entry.kind)}</Muted>
                  )}
                </>
              }
            />
          ))}
        </CardBody>
      )}

      {locked === null ? null : (
        <CardBody>
          <Locked>
            <Stack>
              <B>{format.qty(locked.count, HELD_ITEMS)} held until the final invoice is paid</B>
              <Stack gap="tight">
                {locked.items.map((item) => (
                  <Small key={item.title}>{item.title}</Small>
                ))}
              </Stack>
              {locked.milestone === null ? null : (
                <Small>
                  <Linkish asChild>
                    <Link to="/app/projects">{milestoneLine(locked.milestone, format)}</Link>
                  </Linkish>
                </Small>
              )}
            </Stack>
          </Locked>
        </CardBody>
      )}
    </Card>
  )
}

/** "Milestone 4 — Launch & handover, $946.00, due 30 Sep 2026". */
function milestoneLine(
  milestone: NonNullable<ProjectFilesView['locked']>['milestone'] & object,
  format: Formatter,
): string {
  const head = `Milestone ${format.number(milestone.seq)} — ${milestone.title}`
  const amount = format.money(milestone.amount)
  if (milestone.due_on === null) return `${head}, ${amount}`
  return `${head}, ${amount}, due ${format.day(milestone.due_on)}`
}

/**
 * "Milestone 2 · Design system and page designs · signed off 24 Jul 2026".
 *
 * The third part is `milestone_delivered_at`, which `files-client.ts` publishes
 * and this card printed nothing for — so a row whose milestone was signed off a
 * month ago read exactly like one whose milestone is still being built, which is
 * the difference `files.html` prints on its first two rows. An instant rather than
 * a day (`packages/shared/src/schemas/files.ts` says why), so it goes through
 * `format.date` in the viewer's own zone.
 */
function milestoneSubOf(entry: FileEntryView, format: Formatter): string | undefined {
  if (entry.milestone_seq === null) return undefined
  const parts = [`Milestone ${format.number(entry.milestone_seq)}`]
  if (entry.milestone_title !== null) parts.push(entry.milestone_title)
  if (entry.milestone_delivered_at !== null) {
    parts.push(`signed off ${format.date(entry.milestone_delivered_at)}`)
  }
  return parts.join(' · ')
}
